/**
 * Projections carry participant ids only; names and scores come from `core` and are
 * joined here so no game ships the roster twice.
 */

import type { CoreBlock, ParticipantId } from '@gsf/contract';

/** A row on the scoreboard: the roster and the ledger, joined. */
export interface Standing {
  readonly id: ParticipantId;
  readonly name: string;
  readonly score: number;
  readonly connected: boolean;
}

/** Every player with their segment score. Join order, not rank — `<Scoreboard>` sorts. */
export function rosterFrom(core: CoreBlock): readonly Standing[] {
  const scores = core.scores.segment;
  return core.players.map((player) => ({
    id: player.id,
    name: player.name,
    score: scores[player.id] ?? 0,
    connected: player.connected,
  }));
}

/** Two accessors over the same join, for the places that hold one id rather than a list. */
export interface Names {
  nameOf(id: ParticipantId | string | null): string | null;
  scoreOf(id: ParticipantId | string | null): number;
}

export function namesFrom(core: CoreBlock): Names {
  const names = new Map(core.players.map((player) => [player.id, player.name]));
  const scores = core.scores.segment;
  return {
    nameOf: (id) => (id === null ? null : (names.get(id as ParticipantId) ?? null)),
    scoreOf: (id) => (id === null ? 0 : (scores[id] ?? 0)),
  };
}
